import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { Severity, DataQuality, Tier2Factor } from '@/types/analytics';
import { Tier2Panel } from './Tier2Panel';

const BG_TINT: Record<Severity, string> = {
  green:  'bg-green-50/50 dark:bg-green-950/30',
  yellow: 'bg-amber-50/50 dark:bg-amber-950/30',
  red:    'bg-red-50/50 dark:bg-red-950/30',
};

function DeltaIndicator({
  rawValue,
  prevValue,
  valueStr,
  higherIsBetter,
}: {
  rawValue: number;
  prevValue: number;
  valueStr: string;
  higherIsBetter: boolean;
}) {
  const delta = rawValue - prevValue;
  const relChange = Math.abs(prevValue) > 0.001 ? Math.abs(delta) / Math.abs(prevValue) : 1;

  // < 1% relative change → neutral
  if (relChange < 0.01) {
    return <span className="text-xs text-muted-foreground ml-1">→</span>;
  }

  const improved = higherIsBetter ? delta > 0 : delta < 0;
  // Arrow shows quality direction (not raw value direction) so lower-is-better metrics
  // show ↓ when value increased (worse) and ↑ when value decreased (better).
  const arrow = improved ? '↑' : '↓';
  const colorClass = improved ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400';

  let deltaStr: string;
  if (valueStr.endsWith('%')) {
    deltaStr = `${Math.abs(delta).toFixed(1)}pts`;
  } else if (valueStr.startsWith('$')) {
    deltaStr = `$${Math.abs(delta).toFixed(2)}`;
  } else if (valueStr.endsWith(' min')) {
    deltaStr = `${Math.abs(delta).toFixed(0)} min`;
  } else {
    deltaStr = Math.abs(delta).toLocaleString(undefined, { maximumFractionDigits: 1 });
  }

  return (
    <span className={`text-sm font-medium ${colorClass} ml-1.5`}>
      {arrow}{deltaStr}
    </span>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  severity: Severity;
  target?: string;
  subtitle?: string;
  dataQuality?: DataQuality;
  /** When true, renders the sparse-data quality badge. Only CPL should set this. */
  showDataQualityBadge?: boolean;
  tier2Factors: Tier2Factor[];
  tier2Data: Record<string, unknown>;
  isOpen: boolean;
  onToggle: () => void;
  rawValue?: number;
  prevValue?: number;
  higherIsBetter?: boolean;
}

export function KpiCard({
  label,
  value,
  severity,
  target,
  subtitle,
  dataQuality,
  showDataQualityBadge = false,
  tier2Factors,
  tier2Data,
  isOpen,
  onToggle,
  rawValue,
  prevValue,
  higherIsBetter,
}: KpiCardProps) {
  const hasTier2 = tier2Factors.length > 0;
  const showDelta =
    rawValue !== undefined &&
    prevValue !== undefined &&
    higherIsBetter !== undefined;

  return (
    <Collapsible.Root open={isOpen} onOpenChange={hasTier2 ? onToggle : undefined} className="h-full">
      <Card className={`${BG_TINT[severity]} ${hasTier2 ? 'cursor-pointer' : ''} transition-shadow hover:shadow-md overflow-hidden h-full min-h-[120px]`}>
        <Collapsible.Trigger asChild disabled={!hasTier2}>
          <div className="p-4 select-none min-h-[120px] flex flex-col justify-between">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {label}
              </span>
              <div className="flex items-center gap-1.5">
                {showDataQualityBadge && dataQuality && dataQuality !== 'complete' && (
                  <span className="text-[10px] text-muted-foreground/60 italic bg-muted px-1.5 py-0.5 rounded leading-none">
                    {dataQuality}
                  </span>
                )}
                {hasTier2 && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                    <span>{isOpen ? 'Hide details' : 'Show details'}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-baseline gap-0">
              <p className="text-3xl font-bold leading-tight">{value}</p>
              {showDelta && (
                <DeltaIndicator
                  rawValue={rawValue!}
                  prevValue={prevValue!}
                  valueStr={value}
                  higherIsBetter={higherIsBetter!}
                />
              )}
            </div>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {target && <p className="text-xs text-muted-foreground/50 mt-0.5">{target}</p>}
          </div>
        </Collapsible.Trigger>

        <Collapsible.Content className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
          <Tier2Panel factors={tier2Factors} data={tier2Data} />
        </Collapsible.Content>
      </Card>
    </Collapsible.Root>
  );
}
